import { gql, useApolloClient } from '@apollo/client';
import { useSelectedOrganization } from 'hooks/useSelectedOrganization';
import {
  DeleteAutoImportMutationOptions,
  useDeleteAllAutoImportsMutation,
  useDeleteAutoImportMutation
} from 'models/graphql';
import React, { useCallback } from 'react';
import { DeleteImporterModalController, DeleteImporterModalControllerProps } from './DeleteImporterModal.controller';

export type DeleteCategoryModalApolloProps = Omit<
  DeleteImporterModalControllerProps,
  'deleteImport' | 'deleteAllImports'
>;

gql`
  mutation DeleteAutoImport($importId: ID!) {
    deleteAutoImport(autoImportId: $importId)
  }
`;

gql`
  mutation DeleteAllAutoImports($workspaceId: ID!) {
    deleteAllAutoImports(workspaceId: $workspaceId)
  }
`;

export const DeleteImporterModalApollo = (props: DeleteCategoryModalApolloProps): JSX.Element => {
  const { cache } = useApolloClient();
  const { selectedWorkspace } = useSelectedOrganization();
  const [deleteImport] = useDeleteAutoImportMutation();
  const [deleteAllImports] = useDeleteAllAutoImportsMutation();
  const workspaceId = selectedWorkspace?.id ?? '';

  const deleteImportMutationHandler = useCallback(
    async (options?: DeleteAutoImportMutationOptions) => {
      const importId = options?.variables?.importId;
      await deleteImport({
        variables: { importId: importId ?? '' }
      });
      cache.evict({ id: cache.identify({ id: importId, __typename: 'AutoImport' }) });
      cache.gc();
    },
    [deleteImport, cache]
  );

  const deleteAllImportMutationHandler = useCallback(async () => {
    await deleteAllImports({
      variables: { workspaceId: workspaceId ?? '' }
    });
    cache.modify({
      id: cache.identify({ id: workspaceId, __typename: 'Workspace' }),
      fields: {
        autoImports() {
          return [];
        }
      }
    });
    cache.gc();
  }, [cache, deleteAllImports, workspaceId]);

  const controllerProps: DeleteImporterModalControllerProps = {
    ...props,
    deleteImport: deleteImportMutationHandler,
    deleteAllImports: deleteAllImportMutationHandler
  };

  return <DeleteImporterModalController {...controllerProps} />;
};
